import { Language as UppercaseLanguage, LANGUAGES } from '@boltenergy-be/shared'
import { LanguageLowercase } from 'store/app/types.ts'
import { getPersistedItem, setPersistedItem } from 'utils/persistedStorage.ts'
import { LANGUAGE_STORAGE_KEY } from 'constants/constants.ts'

/**
 * Type guard to check if the given language is a valid lowercase language
 * @param {string} lng
 * @returns {lng is LanguageLowercase}
 */
export const isValidLowercaseLanguage = (lng: string): lng is LanguageLowercase => {
  return Object.values(LanguageLowercase).some((l) => l === lng)
}

/**
 * Type guard to check if the given language is a valid uppercase language
 * @param {string} lng
 * @returns {lng is UppercaseLanguage}
 */
export const isValidUppercaseLanguage = (lng: string): lng is UppercaseLanguage => {
  return Object.values(LANGUAGES).some((l) => l === lng)
}

/**
 * Converts the given language to uppercase
 * @param {string} lng
 * @returns {UppercaseLanguage}
 */
export const getUpperCaseLanguage = (lng: string): UppercaseLanguage => {
  if (isValidUppercaseLanguage(lng)) return lng
  if (!isValidLowercaseLanguage(lng)) return LANGUAGES.NL

  return lng.toUpperCase() as UppercaseLanguage
}

/**
 * Converts the given language to lowercase
 * @param {string} lng
 * @returns {LanguageLowercase}
 */
export const getLowerCaseLanguage = (lng: string): LanguageLowercase => {
  if (isValidLowercaseLanguage(lng)) return lng
  if (!isValidUppercaseLanguage(lng)) return LanguageLowercase.NL

  return lng.toLowerCase() as LanguageLowercase
}

/**
 * Gets the language to start the app in: the persisted language, else the `lang` search param (persisted for next visits), else Dutch
 * @returns {LanguageLowercase}
 */
export const getInitialLanguage = (): LanguageLowercase => {
  const storedLanguage = getPersistedItem(LANGUAGE_STORAGE_KEY)?.toLowerCase()
  if (storedLanguage && isValidLowercaseLanguage(storedLanguage)) return storedLanguage

  const urlLanguage = new URLSearchParams(window.location.search).get('lang')?.toLowerCase()
  if (urlLanguage && isValidLowercaseLanguage(urlLanguage)) {
    setPersistedItem(LANGUAGE_STORAGE_KEY, urlLanguage, 365)
    return urlLanguage
  }

  return LanguageLowercase.NL
}
